import { useEffect } from 'react';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Hook para limpar automaticamente slots expirados
 * Remove slots que já passaram da data/hora (não marca como cancelado)
 * Só limpa slots da barbearia do owner logado
 * 
 * REGRAS:
 * - Slots de dias passados: deletados imediatamente
 * - Slots de hoje: deletados somente se o horário do slot JÁ PASSOU
 *   (usa comparação com Date objects para evitar problemas de string comparison)
 */
export const useSlotCleanup = (currentUser) => {
  useEffect(() => {
    // Só executar se for owner
    if (!currentUser || currentUser.role !== 'owner') {
      return;
    }

    // Obter barbershopId (suporta formato antigo e novo)
    const barbershopId = currentUser.barbershopId || (currentUser.barbershops && currentUser.barbershops[0]);
    
    if (!barbershopId) {
      return;
    }

    const cleanupExpiredSlots = async () => {
      try {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const today = `${year}-${month}-${day}`; // Formato YYYY-MM-DD (local)

        console.log(`🕐 Iniciando limpeza de slots... Agora: ${today} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);

        // ==========================================
        // 1. Buscar e deletar slots de dias anteriores
        // ==========================================
        const slotsRef = collection(db, 'availableSlots');
        const pastQuery = query(
          slotsRef,
          where('barbershopId', '==', barbershopId),
          where('isBooked', '==', false),
          where('date', '<', today)
        );

        const pastSnapshot = await getDocs(pastQuery);
        
        const pastDeletePromises = pastSnapshot.docs.map(docSnapshot => {
          const data = docSnapshot.data();
          console.log(`🗑️ Removendo slot de dia passado: ${data.date} ${data.time} (id: ${docSnapshot.id})`);
          return deleteDoc(doc(db, 'availableSlots', docSnapshot.id));
        });

        // ==========================================
        // 2. Buscar slots de hoje e deletar APENAS os que já passaram
        //    Usa Date objects para comparação segura (evita problemas de string)
        // ==========================================
        const todayQuery = query(
          slotsRef,
          where('barbershopId', '==', barbershopId),
          where('isBooked', '==', false),
          where('date', '==', today)
        );

        const todaySnapshot = await getDocs(todayQuery);
        
        // Filtrar e deletar slots de hoje cujo horário JÁ PASSOU
        const expiredTodaySlots = todaySnapshot.docs.filter(docSnapshot => {
          const data = docSnapshot.data();
          const slotTime = data.time; // Formato "HH:MM"
          
          // Criar Date object para o horário do slot (comparação segura)
          const [slotHours, slotMinutes] = slotTime.split(':').map(Number);
          const slotDateTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), slotHours, slotMinutes, 0);
          
          const isExpired = now > slotDateTime;
          
          if (isExpired) {
            console.log(`🗑️ Removendo slot expirado de hoje: ${data.date} ${data.time} (id: ${docSnapshot.id}) — horário já passou`);
          } else {
            console.log(`✅ Mantendo slot de hoje: ${data.date} ${data.time} (id: ${docSnapshot.id}) — ainda não passou`);
          }
          
          return isExpired;
        });

        const expiredTodayPromises = expiredTodaySlots.map(docSnapshot =>
          deleteDoc(doc(db, 'availableSlots', docSnapshot.id))
        );

        await Promise.all([...pastDeletePromises, ...expiredTodayPromises]);

        const totalDeleted = pastDeletePromises.length + expiredTodayPromises.length;
        if (totalDeleted > 0) {
          console.log(`✅ Limpeza automática concluída: ${totalDeleted} slot(s) expirado(s) removido(s)`);
        } else {
          console.log(`✅ Limpeza automática: nenhum slot expirado encontrado`);
        }
      } catch (error) {
        console.error('❌ Erro na limpeza de slots:', error);
      }
    };

    // Executar limpeza ao montar (quando o owner abre o app)
    cleanupExpiredSlots();

    // Fallback: executar a cada 12 horas (caso o app fique aberto o dia todo)
    // Economiza leituras do Firebase no plano gratuito
    const interval = setInterval(cleanupExpiredSlots, 12 * 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [currentUser]);
};
